import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import tw, { css, styled } from 'twin.macro'
import kebabCase from 'lodash/kebabCase'
import Text from './text'
import { global } from '../styles/global'
import Link from './link'
import Image from './image'
import SearchBar from './search-bar'
import Heading from './heading'
import { act } from 'react'

const ProductsAccordion = ({
  style,
  products,
  title,
  searchBar,
  searchIcon,
  searchPlaceholder,
  tabOne,
  tabTwo,
  otherDocuments,
}) => {
  //const [displayCount, setDisplayCount] = useState(products.length)
  const [filteredProducts, setFilteredProducts] = useState(products)
  const [noProductsFound, setNoProductsFound] = useState(false)
  const [activeTab, setActiveTab] = useState('tabOne')

  const filterProducts = (searchText) => {
    const filtered = products.filter((product) =>
      product.categoryTitle.toLowerCase().includes(searchText)
    )
    setFilteredProducts(filtered)
    setNoProductsFound(filtered.length === 0)
  }

  const displayedArticles = useMemo(() => {
    return filteredProducts?.slice(0, products.length)
  }, [filteredProducts, products.length])

  const renderTabSection = (items) => {
    return (
      <div css={style}>
        <div css={[tw`flex flex-col bg-grey-light`]}>
          {items.map((item, i) => (
            <div
              key={item.categoryTitle + i}
              css={[
                css`
                  ${tw`border-b border-primary-500 bg-white xl:w-full mb-6 border border-primary-100 border-b-0 border-opacity-100`}
                  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1),
                    0px 0px 0px 0px rgba(0, 0, 0, 0.05);
                `,
              ]}
            >
              <div
                css={[
                  css`
                    ${tw`py-12 px-6 flex flex-row justify-between items-center w-full -my-8 outline-none focus:outline-none lg:(pb-6 pt-12)`}
                  `,
                ]}
              >
                <div css={tw`flex flex-row items-center`}>
                  <Image image={item.categoryIcon} style={[tw`w-8 h-8 mr-2`]} />
                  <Heading
                    headingType="h6"
                    content={item.categoryTitle}
                    style={[
                      tw`w-8/10 whitespace-nowrap text-left text-primary-500 text-opacity-100 font-semibold col-start-2 md:(w-fit-content)`,
                      item.categoryTitle.length > 30 && tw`whitespace-normal`,
                    ]}
                  />
                </div>
              </div>

              <div
                css={tw`mx-6 border-b border-primary-500 border-opacity-10 lg:(mt-6 mb-4 mx-6)`}
              />

              <div
                css={[tw`max-h-accordion overflow-hidden transition-all duration-300 ease-in-out`]}
              >
                <div css={tw`pb-6 pt-4 lg:(pt-0) flex justify-start`}>
                  <div id={kebabCase(item.categoryTitle)} style={tw`w-full`}>
                    <div css={tw`flex w-full flex-col justify-between px-6 md:px-6 lg:flex-row`}>
                      {item.documentType.map((document, x) => (
                        <div key={x} css={tw`flex flex-col w-full lg:(py-0 w-52) xl:(w-72) xxl:(w-89)`}>
                          {activeTab === 'tabOne' && <div
                            css={[
                              tw`bg-primary-50 flex justify-center items-center py-2 mb-4`,
                              document.callToAction.length === 0 && tw`mb-6`,
                              x === item.documentType.length - 1 &&
                                document.callToAction.length === 0 &&
                                tw`mb-0`,
                            ]}
                          >
                            <Image image={document.image} style={tw`w-4 h-4 mr-2 lg:(w-6 h-6)`} />
                            <Text
                              content={document.title}
                              textType={'subtitle-lg'}
                              style={tw`font-bold`}
                            />
                          </div>}
                          {activeTab === 'tabOne' && document.callToAction.length > 0 ? (
                            <div css={tw`flex flex-col justify-start items-start mb-6 lg:(mb-0)`}>
                              <ul>
                                {document.callToAction.map((cta, z) =>
                                  cta.subLinks.map((button, w) => (
                                    <li key={z + w}>
                                      <Link
                                        to={button.link}
                                        localized={false}
                                        style={global`base.hover-underline`(tw`bg-white underline`)}
                                      >
                                        <Text
                                          content={'• ' + button.label}
                                          style={tw`text-secondary-500 text-sm`}
                                        />
                                      </Link>
                                    </li>
                                  ))
                                )}
                              </ul>
                            </div>
                          ) : (
                            <div css={tw`flex flex-col justify-start items-start md:whitespace-nowrap`}>
                              <ul>
                                {document.callToAction.map((cta, y) => (
                                  <li key={y}>
                                    <Link
                                      to={cta.link}
                                      localized={false}
                                      style={global`base.hover-underline`(tw`bg-white underline`)}
                                    >
                                      <Text
                                        content={'• ' + cta.label}
                                        style={tw`text-secondary-500 text-sm`}
                                      />
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const renderTabContent = () => {
    if (activeTab === 'tabOne') {
      return renderTabSection(displayedArticles)
    } else if (activeTab === 'tabTwo') {
      return renderTabSection(otherDocuments)
    }
  }

  return (
    <>
      <div css={tw`hidden lg:(block pb-8 sticky flex flex-col z-10 bg-grey-light top-20)`}>
        {searchBar && (
          <SearchBar
            data={products}
            icon={searchIcon}
            onSearch={filterProducts}
            searchPlaceholder={searchPlaceholder}
          />
        )}
      </div>

      {searchBar && (
        <div css={tw`pb-8 sticky flex flex-col top-18 z-10 bg-grey-light lg:hidden`}>
          <SearchBar
            style={tw`mt-8`}
            data={products}
            icon={searchIcon}
            onSearch={filterProducts}
            searchPlaceholder={searchPlaceholder}
          />
        </div>
      )}

      <div css={tw`flex justify-center mb-8 whitespace-nowrap md:(justify-start w-69.5)`}>
        <button
          onClick={() => setActiveTab('tabOne')}
          css={[
            css`
              ${tw`px-4.5 py-3.5 font-bold w-3/6 md:w-full`}
              ${activeTab === 'tabOne' ? tw`text-primary-500` : tw`text-secondary-910`}
            border-bottom: ${activeTab === 'tabOne' ? '2px solid #00BAC7' : '2px solid #e5e5e5'};
            `,
          ]}
        >
          <Text content={tabOne} style={tw`text-sm`} />
        </button>
        <button
          onClick={() => setActiveTab('tabTwo')}
          css={[
            css`
              ${tw`px-4.5 py-3.5 font-bold w-3/6  md:w-full`}
              ${activeTab === 'tabTwo' ? tw`text-primary-500` : tw`text-secondary-910`}
            border-bottom: ${activeTab === 'tabTwo' ? '2px solid #00BAC7' : '2px solid #e5e5e5'};
            `,
          ]}
        >
          <Text content={tabTwo} style={tw`text-sm`} />
        </button>
      </div>

      {noProductsFound ? (
        <Text content={'No products found'} style={tw`font-bold text-base mt-8`} />
      ) : (
        renderTabContent()
      )}
    </>
  )
}

ProductsAccordion.propTypes = {
  searchBar: PropTypes.bool,
}

export default ProductsAccordion
